import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

import type { DiscountWizardOptions, InitialWizardState as DiscountInitialState } from './discount-wizard/types';
import type { InitialWizardState as SmartTagInitialState, SmartTagWizardOptions } from './smart-tag-wizard/types';
import type {
  WizardState,
  WizardRegistry,
  WizardKey,
  ShowWizardOptions,
  WizardContextValue,
  WizardProviderProps,
} from './types';

const WizardContext = createContext<WizardContextValue | undefined>(undefined);

const createInitialWizardState = <TOptions, TInitialState = unknown>(
  defaultOptions: TOptions
): WizardState<TOptions, TInitialState> => ({
  entityId: undefined,
  initialState: undefined,
  isActive: false,
  options: defaultOptions,
});

export function WizardProvider({ children }: WizardProviderProps): JSX.Element {
  const [wizardRegistry, setWizardRegistry] = useState<WizardRegistry>({
    discount: createInitialWizardState<DiscountWizardOptions, DiscountInitialState>({}),
    smartTag: createInitialWizardState<SmartTagWizardOptions, SmartTagInitialState>({}),
  });

  const getWizardState = useCallback(
    <K extends WizardKey>(wizardKey: K): WizardRegistry[K] => wizardRegistry[wizardKey],
    [wizardRegistry]
  );

  const showWizard = useCallback(<K extends WizardKey>(wizardKey: K, options?: ShowWizardOptions<K>) => {
    setWizardRegistry((prev) => ({
      ...prev,
      [wizardKey]: {
        ...prev[wizardKey],
        entityId: options?.entityId ?? prev[wizardKey].entityId,
        isActive: true,
        initialState: options?.initialState,
        options: options ?? prev[wizardKey].options,
      },
    }));
  }, []);

  const hideWizard = useCallback((wizardKey: WizardKey) => {
    setWizardRegistry((prev) => ({
      ...prev,
      [wizardKey]: {
        entityId: undefined,
        initialState: undefined,
        isActive: false,
        options: prev[wizardKey].options,
      },
    }));
  }, []);

  const value = useMemo(
    () => ({
      getWizardState,
      showWizard,
      hideWizard,
    }),
    [getWizardState, showWizard, hideWizard]
  );

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
}

export function useWizard(): WizardContextValue {
  const context = useContext(WizardContext);
  if (context === undefined) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
}
